import { Spinner } from '@concord/ui';
import { useMutation } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import s from './help.module.css';

interface HelpAnswer {
  answer: string;
  outOfScope: boolean;
  sources: { title: string; url: string | null }[];
}

/**
 * Ask AI (navigators only): one question about using Concord, answered from the user guide
 * and this help. Only the latest answer is shown; nothing is kept.
 */
export function AskAi({ screen }: { screen: string }) {
  const [question, setQuestion] = useState('');
  const ask = useMutation({
    mutationFn: (q: string) =>
      api<HelpAnswer>('/staff/help/ask', { method: 'POST', body: { question: q, screen } }),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const q = question.trim();
    if (q.length >= 3 && !ask.isPending) ask.mutate(q);
  }

  return (
    <section className={s.ask} aria-label="Ask AI">
      <h3>Ask AI</h3>
      <form onSubmit={submit} className={s.askForm}>
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={500}
          placeholder="How do I release a case?"
          aria-label="Your question about using Concord"
        />
        <button type="submit" className="btn" disabled={ask.isPending || question.trim().length < 3}>
          Ask
        </button>
      </form>
      <p className={s.askNote}>
        Answers come only from the Concord user guide. Don't include patient details; questions aren't saved.
      </p>
      {ask.isPending && (
        <div className={s.askBusy} role="status">
          <Spinner /> Looking in the guide…
        </div>
      )}
      {ask.isError && (
        <p className={s.askAnswer} role="alert">
          {errorMessage(ask.error)}
        </p>
      )}
      {ask.data && !ask.isPending && (
        <div className={s.askAnswer} aria-live="polite" data-out-of-scope={ask.data.outOfScope || undefined}>
          <Answer text={ask.data.answer} />
          {ask.data.sources.length > 0 && (
            <div className={s.askSources}>
              From the guide:
              <ul>
                {ask.data.sources.map((src) => (
                  <li key={src.url}>
                    <a href={src.url ?? undefined} target="_blank" rel="noopener">
                      {src.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/** A small, safe subset of Markdown: paragraphs, numbered and bullet lists, **bold**. No HTML. */
function Answer({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  const flush = () => {
    if (!list) return;
    // biome-ignore lint/suspicious/noArrayIndexKey: a static answer; items never reorder
    const items = list.items.map((it, i) => <li key={i}>{bold(it)}</li>);
    blocks.push(list.ordered ? <ol key={blocks.length}>{items}</ol> : <ul key={blocks.length}>{items}</ul>);
    list = null;
  };
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    const ordered = /^\d+[.)]\s+(.*)$/.exec(line);
    const bullet = /^[-*•]\s+(.*)$/.exec(line);
    if (ordered || bullet) {
      const isOrdered = Boolean(ordered);
      if (list && list.ordered !== isOrdered) flush();
      list ??= { ordered: isOrdered, items: [] };
      list.items.push((ordered ?? bullet)![1]!);
    } else {
      flush();
      if (line) blocks.push(<p key={blocks.length}>{bold(line)}</p>);
    }
  }
  flush();
  return <>{blocks}</>;
}

function bold(line: string): ReactNode[] {
  // biome-ignore lint/suspicious/noArrayIndexKey: a static answer; parts never reorder
  return line.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));
}
